import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { 
  Account, 
  AccountBalancesAggregatedResponse,
  Transaction, 
  Currency,
  TransactionFilters,
  FinancialRunway,
  ReconciliationStatus
} from '../types';
import {
  PlaidConnection,
  PlaidConnectionStatus,
  PlaidSyncState,
  PlaidAccountBalance,
  SyncTransactionsResponse,
  CreateLinkTokenResponse,
  ExchangePublicTokenResponse,
  GetConnectionsResponse,
  GetConnectionStatusResponse,
  GetAccountBalancesResponse,
  DisconnectAccountResponse,
  BulkSyncTransactionsResponse,
  PlaidLinkMetadata
} from '../types/plaid';
import {
  MonoConnection,
  MonoSyncState,
  ExchangeMonoTokenResponse,
  BulkSyncMonoTransactionsResponse,
  DisconnectMonoResponse
} from '../types/mono';
import { 
  defaultAccounts,
  reconciliationStatus as mockReconciliationStatus,
  dailyFinancialData,
  monthlyFinancialData
} from '../data/mockData';

import { calculateAccountReconciliationData, ReconciliationData } from '../utils/reconciliation';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/storage';
import { useAsyncList } from '../hooks/useAsyncState';

// Import Firebase createCallable from the service file
import { createCallable } from '../services/firebase';

// Import the transaction service
import transactionService, { TransactionCreateData, TransactionUpdateData } from '../services/transactionService';

// Import the account service
import accountService, { CreateAccountInput, UpdateAccountInput, AccountFilters } from '../services/accountService';

// Import API functions
import { plaidApi, monoApi, transactionApi } from '../services/api';

// Import organization context
import { useOrganization } from './OrganizationContext';




// Define new storage keys for import state
const IMPORT_STORAGE_KEYS = {
  REVIEW_TRANSACTIONS: 'finstack_review_transactions',
  SHOW_IMPORT_REVIEW: 'finstack_show_import_review'
};

interface FinanceContextType {
  // State
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  lastSyncTimestamp: Date | null;
  
  // Alias properties for backward compatibility
  transactionsLoading: boolean;
  transactionsError: string | null;
  
  // Status-based transaction arrays
  statusReviewTransactions: Transaction[];
  statusApprovedTransactions: Transaction[];
  statusDeclinedTransactions: Transaction[];
  
  
  // Transaction operations - All pure async
  loadTransactions: () => Promise<Transaction[]>;
  createTransaction: (data: TransactionCreateData) => Promise<Transaction>;
  updateTransaction: (id: string, data: Omit<TransactionUpdateData, 'id'>) => Promise<Transaction>;
  deleteTransaction: (id: string) => Promise<void>;
  deleteTransactions: (ids: string[]) => Promise<void>;
  deleteMultipleTransactions: (ids: string[]) => Promise<void>; // Alias for backward compatibility
  searchTransactions: (term: string) => Promise<Transaction[]>;
  refreshTransactions: () => Promise<void>;
  bulkImportTransactions: (transactions: TransactionCreateData[], skipDuplicates?: boolean) => Promise<any>;
  
  // Status-based transaction operations
  approveTransaction: (transactionId: string, userId?: string) => Promise<Transaction>;
  declineTransaction: (transactionId: string, userId?: string, reason?: string) => Promise<Transaction>;
  loadReviewTransactions: (pagination?: { limit?: number; offset?: number }) => Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }>;
  loadApprovedTransactions: (pagination?: { limit?: number; offset?: number }) => Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }>;
  loadDeclinedTransactions: (pagination?: { limit?: number; offset?: number }) => Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }>;
  getStatusCounts: () => Promise<any>;
  
  // Legacy method for backward compatibility
  addTransaction: (transaction: any) => Promise<Transaction>;
  
  // Split transaction operations
  splitTransaction: (request: { transactionId: string; splits: any[] }) => Promise<any>;
  getTransactionSplits: (parentId: string) => Promise<Transaction[]>;
  
  // Cache operations
  getCacheMetrics: () => any;
  getCacheHealth: () => any;
  warmCache: () => Promise<void>;
  resetCacheMetrics: () => void;
  
  // Currency operations
  currencies: Currency[];
  currenciesLoading: boolean;
  currenciesError: string | null;
  enabledCurrencies: Currency[];
  selectedCurrency: Currency;
  changeCurrency: (currencyCode: string) => Promise<void>;
  formatAmount: (amount: number) => string;
  
  // Account state
  accounts: Account[];
  accountsLoading: boolean;
  accountsError: string | null;
  
  // Currency-aware account state (for dashboard display)
  currencyAwareAccountData: AccountBalancesAggregatedResponse | null;
  currencyAwareAccountsLoading: boolean;
  currencyAwareAccountsError: string | null;
  
  // Account operations - Backend integrated
  loadAccounts: () => Promise<Account[]>;
  loadCurrencyAwareAccounts: (targetCurrency?: string) => Promise<AccountBalancesAggregatedResponse>;
  addAccount: (account: Omit<Account, 'id' | 'lastUpdated'>) => Promise<void>;
  updateAccount: (id: string, account: Partial<Account>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  getAccountById: (id: string) => Promise<Account | null>;
  getAccountCount: (filters?: AccountFilters) => Promise<number>;
  checkAccountExists: (name: string, institution: string) => Promise<boolean>;
  refreshAccounts: () => Promise<void>;
  
  // Legacy state (kept for backward compatibility)
  filters: TransactionFilters;
  setFilters: (filters: TransactionFilters) => void;
  financialRunway: FinancialRunway;
  reconciliationStatus: ReconciliationStatus;
  accountReconciliationData: {
    [accountId: string]: ReconciliationData;
  };
  dailyFinancialData: { labels: string[]; income: number[]; expenses: number[] };
  monthlyFinancialData: { labels: string[]; income: number[]; expenses: number[] };
  dashboardLoading: boolean;
  dashboardError: string | null;
  
  // Dashboard data operations
  loadDashboardData: (currency: string, timeRange: string) => Promise<void>;
  
  // Financial runway operations
  loadFinancialRunway: (currency?: string) => Promise<FinancialRunway>;
  
  // Import state persistence
  reviewTransactions: Array<{id: string, transaction: Omit<Transaction, 'id'>, prediction: any}> | null;
  setReviewTransactions: (transactions: Array<{id: string, transaction: Omit<Transaction, 'id'>, prediction: any}> | null) => void;
  showImportReview: boolean;
  setShowImportReview: (show: boolean) => void;
  
  // Plaid functionality
  plaidConnections: PlaidConnection[];
  plaidConnectionsLoading: boolean;
  plaidConnectionsError: string | null;
  plaidSyncState: PlaidSyncState;
  plaidAccountBalances: PlaidAccountBalance[];
  plaidAccountBalancesLoading: boolean;
  plaidAccountBalancesError: string | null;
  
  // Plaid operations (all wrapped in useCallback)
  loadPlaidConnections: () => Promise<PlaidConnection[]>;
  syncPlaidTransactions: (connectionId?: string) => Promise<SyncTransactionsResponse>;
  connectPlaidAccount: (publicToken: string, metadata?: PlaidLinkMetadata) => Promise<ExchangePublicTokenResponse>;
  disconnectPlaidAccount: (itemId: string, reason?: string) => Promise<DisconnectAccountResponse>;
  getPlaidStatus: (connectionId: string) => Promise<GetConnectionStatusResponse>;
  loadPlaidAccountBalances: (connectionId?: string) => Promise<PlaidAccountBalance[]>;
  bulkSyncPlaidTransactions: (connectionIds?: string[], forceFullSync?: boolean) => Promise<BulkSyncTransactionsResponse>;
  createPlaidLinkToken: () => Promise<CreateLinkTokenResponse>;
  testPlaidConnection: () => Promise<boolean>;
  
  // Mono functionality
  monoConnections: MonoConnection[];
  monoConnectionsLoading: boolean;
  monoConnectionsError: string | null;
  monoSyncState: MonoSyncState;
  
  // Mono operations (all wrapped in useCallback)
  loadMonoConnections: () => Promise<MonoConnection[]>;
  syncMonoTransactions: (connectionIds?: string[]) => Promise<BulkSyncMonoTransactionsResponse>;
  connectMonoAccount: (authCode: string) => Promise<ExchangeMonoTokenResponse>;
  disconnectMonoAccount: (connectionId: string) => Promise<DisconnectMonoResponse>;
  bulkSyncMonoTransactions: (connectionIds?: string[], forceFullSync?: boolean) => Promise<BulkSyncMonoTransactionsResponse>;
  
  // Utility functions
  reset: () => void;
}

const FinanceContext = createContext<FinanceContextType | undefined>(undefined);

// Initial filter state
const initialFilters: TransactionFilters = {
  dateRange: {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  },
  categories: [], // Ensure this is an array of strings, not Category objects
  accounts: [],
  types: [],
  minAmount: null,
  maxAmount: null,
  searchTerm: '',
  reconciled: null
};

interface FinanceProviderProps {
  children: ReactNode;
}

export const FinanceProvider: React.FC<FinanceProviderProps> = ({ children }) => {
  const { organization } = useOrganization();
  const previousOrgIdRef = useRef<string | null>(null);

  // Stable async function for transactions
  const getTransactions = useCallback(
    () => transactionService.getAll().then(response => response.transactions),
    []
  );

  // Transaction state management using the async list hook
  const {
    state: { data: transactions, loading, error },
    fetchItems: fetchTransactionsInternal,
    reset,
    setData,
    addItem,
    updateItem,
    removeItem,
    removeItems
  } = useAsyncList<Transaction>(getTransactions, []);

  // Status-based transaction state
  const [reviewTransactionsList, setReviewTransactionsList] = useState<Transaction[]>([]);
  const [approvedTransactionsList, setApprovedTransactionsList] = useState<Transaction[]>([]);
  const [declinedTransactionsList, setDeclinedTransactionsList] = useState<Transaction[]>([]);
  
  
  // Track last sync timestamp
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState<Date | null>(
    transactionService.getLastSyncTimestamp()
  );
  
  // Account state management using backend service
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  
  // Currency-aware account state management
  const [currencyAwareAccountData, setCurrencyAwareAccountData] = useState<AccountBalancesAggregatedResponse | null>(null);
  const [currencyAwareAccountsLoading, setCurrencyAwareAccountsLoading] = useState<boolean>(false);
  const [currencyAwareAccountsError, setCurrencyAwareAccountsError] = useState<string | null>(null);
  
  // Load transactions on mount
  useEffect(() => {
    let mounted = true;
    
    const loadInitialTransactions = async () => {
      try {
        await fetchTransactionsInternal();
        if (mounted) {
          setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
        }
      } catch (error) {
        if (mounted) {
          console.error('Failed to load transactions on mount:', error);
        }
      }
    };

    loadInitialTransactions();

    return () => {
      mounted = false;
    };
  }, [fetchTransactionsInternal]);

  // Background sync - validate cache periodically with intelligent cache warming
  useEffect(() => {
    let mounted = true;
    
    const backgroundSync = async () => {
      try {
        // Check cache health first
        const cacheHealth = transactionService.getCacheHealth();
        
        // Warm cache if it's getting stale
        if (cacheHealth.status === 'stale') {
          await transactionService.warmCache();
        }
        
        const validation = await transactionService.validateCache();
        if (!validation.isValid && mounted) {
          console.warn('Background sync detected transaction cache issues:', validation.issues);
          
          // Auto-refresh if we detect phantom transactions or major inconsistencies
          const hasPhantomTransactions = validation.issues.some(issue => 
            issue.includes('phantom transactions') || issue.includes('count mismatch')
          );
          
          if (hasPhantomTransactions) {
            console.log('Auto-refreshing transactions due to phantom data...');
            const refreshedTransactions = await transactionService.forceRefresh();
            if (mounted) {
              setData(refreshedTransactions);
              setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
            }
          }
        }
        
        // Log cache performance metrics periodically (every 10th sync)
        if (transactionService.getCacheMetrics().validationCount % 10 === 0) {
          const metrics = transactionService.getCacheMetrics();
          console.debug('Transaction cache performance:', {
            hitRate: `${metrics.hitRate}%`,
            totalRequests: metrics.hitCount + metrics.missCount,
            validations: metrics.validationCount,
            cacheAge: `${metrics.cacheAge || 0} minutes`
          });
        }
      } catch (error) {
        // Silently log background sync errors to avoid spamming console
        if (mounted) {
          console.debug('Background transaction sync failed:', error);
        }
      }
    };

    // Run background sync every 20 minutes
    const interval = setInterval(backgroundSync, 1200000);
    
    // Run initial background sync after 20 seconds
    const timeout = setTimeout(backgroundSync, 20000);

    return () => {
      mounted = false;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [setData]);

  // Detect organization changes and reload data
  useEffect(() => {
    const currentOrgId = organization?.id || null;
    const previousOrgId = previousOrgIdRef.current;

    // If organization changed (and it's not the initial load)
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      console.log('[FinanceContext] Organization changed, reloading transaction data');
      
      // Reset transaction state and reload
      reset();
      
      // Clear status-based transaction arrays
      setReviewTransactionsList([]);
      setApprovedTransactionsList([]);
      setDeclinedTransactionsList([]);
      
      // Reset account states
      setAccounts([]);
      setCurrencyAwareAccountData(null);
      
      // Load fresh data for the new organization
      if (currentOrgId) {
        fetchTransactionsInternal().then(() => {
          setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
        }).catch(error => {
          console.error('[FinanceContext] Failed to reload transactions after org switch:', error);
        });
      }
    }

    // Update the ref for next comparison
    previousOrgIdRef.current = currentOrgId;
  }, [organization?.id, reset, fetchTransactionsInternal]);

  // Categories are now only associated with GL accounts
  
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState<boolean>(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  // Fetch currencies from backend on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      setCurrenciesLoading(true);
      setCurrenciesError(null);
      try {
        const getCurrencies = createCallable('getCurrencies');
        const result = await getCurrencies();
        if (Array.isArray(result.data)) {
          setCurrencies(result.data as Currency[]);
        }
      } catch (error: any) {
        console.error('Failed to fetch currencies:', error);
        setCurrenciesError('Failed to load currencies');
      } finally {
        setCurrenciesLoading(false);
      }
    };
    fetchCurrencies();
  }, []);

  // Compute enabled currencies based on user selection from localStorage
  const enabledCurrencies = React.useMemo(() => {
    const defaultEnabled = currencies.map(currency => currency.code);
    const savedEnabled = loadFromStorage<string[]>(STORAGE_KEYS.ENABLED_CURRENCIES, defaultEnabled);
    return currencies.filter(currency => savedEnabled.includes(currency.code));
  }, [currencies]);

  const [selectedCurrency, setSelectedCurrency] = useState<Currency | undefined>(undefined);

  const [filters, setFilters] = useState<TransactionFilters>(initialFilters);
  const [financialRunway, setFinancialRunway] = useState<FinancialRunway>({
    months: 0,
    burnRate: 0,
    totalSavings: 0,
    currentBalance: 0,
    isProfitable: false,
    tooltipText: '',
    loading: false,
    error: null
  });
  const [reconciliationStatus, setReconciliationStatus] = useState<ReconciliationStatus>(mockReconciliationStatus);
  // Dynamic reconciliation data calculation based on accounts
  const accountReconciliationData = useMemo(() => {
    return calculateAccountReconciliationData(accounts);
  }, [accounts]);
  
  // Import state persistence
  
  const [reviewTransactions, setReviewTransactions] = useState<Array<{id: string, transaction: Omit<Transaction, 'id'>, prediction: any}> | null>(() => {
    return loadFromStorage(IMPORT_STORAGE_KEYS.REVIEW_TRANSACTIONS, null);
  });
  
  const [showImportReview, setShowImportReview] = useState<boolean>(() => {
    return loadFromStorage(IMPORT_STORAGE_KEYS.SHOW_IMPORT_REVIEW, false);
  });

  // Dashboard financial data state
  const [dashboardData, setDashboardData] = useState<{
    dailyData: { labels: string[]; income: number[]; expenses: number[] };
    monthlyData: { labels: string[]; income: number[]; expenses: number[] };
    totals: { totalIncome: number; totalExpenses: number; netIncome: number };
  }>({
    dailyData: dailyFinancialData,
    monthlyData: monthlyFinancialData,
    totals: { totalIncome: 0, totalExpenses: 0, netIncome: 0 }
  });
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);


  // Plaid state
  const [plaidConnections, setPlaidConnections] = useState<PlaidConnection[]>([]);
  const [plaidConnectionsLoading, setPlaidConnectionsLoading] = useState<boolean>(false);
  const [plaidConnectionsError, setPlaidConnectionsError] = useState<string | null>(null);
  
  const [plaidSyncState, setPlaidSyncState] = useState<PlaidSyncState>({
    isSyncing: false,
    lastSync: undefined,
    result: undefined,
    error: undefined
  });
  
  const [plaidAccountBalances, setPlaidAccountBalances] = useState<PlaidAccountBalance[]>([]);
  const [plaidAccountBalancesLoading, setPlaidAccountBalancesLoading] = useState<boolean>(false);
  const [plaidAccountBalancesError, setPlaidAccountBalancesError] = useState<string | null>(null);

  // Mono state
  const [monoConnections, setMonoConnections] = useState<MonoConnection[]>([]);
  const [monoConnectionsLoading, setMonoConnectionsLoading] = useState<boolean>(false);
  const [monoConnectionsError, setMonoConnectionsError] = useState<string | null>(null);
  
  const [monoSyncState, setMonoSyncState] = useState<MonoSyncState>({
    isSyncing: false,
    progress: undefined,
    result: undefined,
    error: undefined
  });

  // Load accounts from backend on mount
  useEffect(() => {
    let mounted = true;
    
    const loadInitialAccounts = async () => {
      try {
        setAccountsLoading(true);
        setAccountsError(null);
        const backendAccounts = await accountService.getAll();
        if (mounted) {
          setAccounts(backendAccounts);
        }
      } catch (error: any) {
        if (mounted) {
          const errorMessage = error?.message || 'Failed to load accounts';
          setAccountsError(errorMessage);
          console.error('Failed to load accounts on mount:', error);
        }
      } finally {
        if (mounted) {
          setAccountsLoading(false);
        }
      }
    };

    loadInitialAccounts();

    return () => {
      mounted = false;
    };
  }, []);

  // Load currency-aware accounts when selectedCurrency changes
  useEffect(() => {
    let mounted = true;

    const loadCurrencyAccounts = async () => {
      if (!selectedCurrency?.code) return;
      
      try {
        setCurrencyAwareAccountsLoading(true);
        setCurrencyAwareAccountsError(null);
        const aggregatedData = await transactionApi.getAccountBalances(selectedCurrency.code);
        if (mounted) {
          setCurrencyAwareAccountData(aggregatedData);
        }
      } catch (error: any) {
        if (mounted) {
          const errorMessage = error?.message || 'Failed to load currency-aware accounts';
          setCurrencyAwareAccountsError(errorMessage);
          console.error('Failed to load currency-aware accounts:', error);
        }
      } finally {
        if (mounted) {
          setCurrencyAwareAccountsLoading(false);
        }
      }
    };

    loadCurrencyAccounts();

    return () => {
      mounted = false;
    };
  }, [selectedCurrency?.code]);

  // No longer saving transactions to localStorage - they're managed by the service cache
  // No longer saving categories to localStorage - they're only associated with GL accounts now
  
  
  useEffect(() => {
    if (reviewTransactions && reviewTransactions.length > 0) {
      saveToStorage(IMPORT_STORAGE_KEYS.REVIEW_TRANSACTIONS, reviewTransactions);
    } else {
      localStorage.removeItem(IMPORT_STORAGE_KEYS.REVIEW_TRANSACTIONS);
    }
  }, [reviewTransactions]);
  
  useEffect(() => {
    if (showImportReview) {
      saveToStorage(IMPORT_STORAGE_KEYS.SHOW_IMPORT_REVIEW, true);
    } else {
      localStorage.removeItem(IMPORT_STORAGE_KEYS.SHOW_IMPORT_REVIEW);
    }
  }, [showImportReview]);


  /**
   * Load all transactions from the backend
   */
  const loadTransactions = useCallback(async (): Promise<Transaction[]> => {
    const result = await fetchTransactionsInternal();
    setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
    return result;
  }, [fetchTransactionsInternal]);

  /**
   * Load dashboard financial data from backend
   */
  const loadDashboardData = useCallback(async (currency: string, timeRange: string): Promise<void> => {
    try {
      setDashboardLoading(true);
      setDashboardError(null);
      
      const data = await transactionApi.getDashboardFinancialData(currency, timeRange);
      setDashboardData(data);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load dashboard data';
      setDashboardError(errorMessage);
      console.error('Failed to load dashboard data:', error);
      throw error;
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  /**
   * Load financial runway data from backend with currency conversion
   */
  const loadFinancialRunway = useCallback(async (currency?: string): Promise<FinancialRunway> => {
    try {
      setFinancialRunway(prev => ({
        ...prev,
        loading: true,
        error: null
      }));
      
      const targetCurrency = currency || selectedCurrency?.code || 'USD';
      const data = await transactionApi.getFinancialRunway(targetCurrency);
      
      const updatedRunway: FinancialRunway = {
        months: data.months,
        burnRate: data.burnRate,
        totalSavings: data.totalSavings,
        currentBalance: data.currentBalance,
        isProfitable: data.isProfitable,
        tooltipText: data.tooltipText,
        loading: false,
        error: null
      };
      
      setFinancialRunway(updatedRunway);
      return updatedRunway;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load financial runway';
      setFinancialRunway(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      console.error('Failed to load financial runway:', error);
      throw error;
    }
  }, [selectedCurrency]);

  // Create new transaction
  const createTransaction = useCallback(async (data: TransactionCreateData): Promise<Transaction> => {
    try {
      const newTransaction = await transactionService.create(data);
      addItem(newTransaction);
      return newTransaction;
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw error;
    }
  }, [addItem]);

  // Legacy wrapper for addTransaction - maintained for backward compatibility
  const addTransaction = useCallback(async (transaction: any): Promise<Transaction> => {
    // Find matching account by ID or name for backward compatibility
    let accountId = '';
    let accountName = '';
    
    if (transaction.account) {
      if (typeof transaction.account === 'string') {
        const matchingAccount = accounts.find(a => a.name === transaction.account);
        accountId = matchingAccount?.id || '';
        accountName = transaction.account;
      } else if (transaction.account && 'id' in transaction.account) {
        accountId = transaction.account.id;
        accountName = transaction.account.name;
      }
    } else if ('accountId' in transaction && 'accountName' in transaction && transaction.accountId && transaction.accountName) {
      accountId = transaction.accountId;
      accountName = transaction.accountName;
    }
    
    // Validate required fields
    if (!transaction.userTransactionId) {
      throw new Error('userTransactionId is required');
    }
    
    // Create transaction data for the service
    const transactionData: TransactionCreateData = {
      userTransactionId: transaction.userTransactionId,
      timestamp: transaction.timestamp,
      description: transaction.description,
      amount: typeof transaction.amount === 'number' ? transaction.amount : parseFloat(String(transaction.amount)),
      type: transaction.type,
      reconciled: transaction.reconciled || false,
      currency: transaction.currency || 'USD', // Default to USD if not provided
      accountId,
      accountName,
      glAccountNumber: transaction.glAccountNumber || 'DEFAULT',
      glAccountName: transaction.glAccountName || 'Default Account',
      glSubcategory: transaction.glSubcategory || '',
      category: transaction.category || '',
      ruleBasedAssignment: transaction.ruleBasedAssignment || false,
      appliedRuleId: transaction.appliedRuleDetails?.id,
      appliedRuleName: transaction.appliedRuleDetails?.description,
      aiPredictionConfidence: transaction.aiPredictionConfidence,
      aiPredictionUsed: transaction.aiPredictionUsed
    };
    
    return createTransaction(transactionData);
  }, [accounts, createTransaction]);

  /**
   * Update an existing transaction using the backend service
   */
  // Update existing transaction
  const updateTransaction = useCallback(async (id: string, data: Omit<TransactionUpdateData, 'id'>): Promise<Transaction> => {
    try {
      const updatedTransaction = await transactionService.update({ id, ...data });
      updateItem(updatedTransaction, (transaction) => transaction.id);
      return updatedTransaction;
    } catch (error) {
      console.error('Failed to update transaction:', error);
      throw error;
    }
  }, [updateItem]);

  // Delete single transaction
  const deleteTransaction = useCallback(async (id: string): Promise<void> => {
    try {
      await transactionService.delete(id);
      removeItem(id, (transaction) => transaction.id);
    } catch (error: any) {
      console.error('Failed to delete transaction:', error);
      
      // If it's a stale data error, refresh the transaction list
      if (error?.code === 'STALE_DATA') {
        console.log('Refreshing transaction list due to stale data...');
        const refreshResponse = await transactionService.getAll();
        setData(refreshResponse.transactions);
        setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
      }
      
      throw error;
    }
  }, [removeItem, setData]);

  /**
   * Delete multiple transactions in bulk using the backend service
   */
  const deleteTransactions = useCallback(async (ids: string[]): Promise<void> => {
    try {
      if (ids.length === 0) return;
      
      // Find all transactions that will be deleted
      const transactionsToDelete = (transactions || []).filter(t => ids.includes(t.id));
      if (transactionsToDelete.length === 0) return;
      
      await transactionService.deleteBulk(ids);
      removeItems(ids, (t) => t.id);
      
      // Track account balance changes and reconciliation status changes for legacy accounts
      const accountChanges: {[accountId: string]: number} = {};
      let reconciledCount = 0;
      let unreconciledCount = 0;
      
      // Calculate all changes to apply
      transactionsToDelete.forEach(transaction => {
        if (transaction.account && typeof transaction.account === 'object' && 'id' in transaction.account) {
          const accountId = transaction.account.id;
          const amount = transaction.amount;
          const type = transaction.type;
          
          // For income, remove the amount; for expense, add it back
          const balanceAdjustment = type === 'income' ? -amount : Math.abs(amount);
          
          // Accumulate changes per account
          if (!accountChanges[accountId]) {
            accountChanges[accountId] = 0;
          }
          accountChanges[accountId] += balanceAdjustment;
        }
        
        // Track reconciliation status changes
        if (transaction.reconciled) {
          reconciledCount++;
        } else {
          unreconciledCount++;
        }
      });
      
      // Apply all account balance changes at once
      const updatedAccounts = accounts.map(account => {
        if (accountChanges[account.id]) {
          return {
            ...account,
            balance: account.balance + accountChanges[account.id],
            lastUpdated: new Date()
          };
        }
        return account;
      });
      
      // Update accounts
      setAccounts(updatedAccounts);
      
      // Update reconciliation status
      setReconciliationStatus({
        ...reconciliationStatus,
        total: reconciliationStatus.total - transactionsToDelete.length,
        reconciled: reconciliationStatus.reconciled - reconciledCount,
        unreconciled: reconciliationStatus.unreconciled - unreconciledCount
      });
    } catch (error) {
      console.error('Failed to delete transactions:', error);
      throw error;
    }
  }, [transactions, removeItems, accounts, reconciliationStatus, setAccounts, setReconciliationStatus]);

  /**
   * Search transactions using the backend service
   */
  const searchTransactions = useCallback(async (searchTerm: string): Promise<Transaction[]> => {
    try {
      return await transactionService.search(searchTerm);
    } catch (error) {
      console.error('Failed to search transactions:', error);
      throw error;
    }
  }, []);

  /**
   * Refresh transactions from the backend
   */
  const refreshTransactions = useCallback(async (): Promise<void> => {
    try {
      const refreshedTransactions = await transactionService.refresh();
      setData(refreshedTransactions);
      setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
    } catch (error) {
      console.error('Failed to refresh transactions:', error);
      throw error;
    }
  }, [setData]);


  /**
   * Get cache performance metrics
   */
  const getCacheMetrics = () => {
    return transactionService.getCacheMetrics();
  };

  /**
   * Get cache health status
   */
  const getCacheHealth = () => {
    return transactionService.getCacheHealth();
  };

  /**
   * Warm cache in background
   */
  const warmCache = useCallback(async (): Promise<void> => {
    return transactionService.warmCache();
  }, []);

  /**
   * Reset cache performance metrics
   */
  const resetCacheMetrics = (): void => {
    transactionService.resetCacheMetrics();
  };

  /**
   * Bulk import transactions using the backend service
   */
  const bulkImportTransactions = useCallback(async (transactionData: TransactionCreateData[], skipDuplicates: boolean = true): Promise<any> => {
    try {
      const result = await transactionService.bulkImport({
        transactions: transactionData,
        skipDuplicates,
        validateOnly: false
      });
      
      // Refresh transactions after successful import
      if (result.successful && result.successful.length > 0) {
        await refreshTransactions();
      }
      
      return result;
    } catch (error) {
      console.error('Failed to bulk import transactions:', error);
      throw error;
    }
  }, [refreshTransactions]);

  /**
   * Approve a transaction
   */
  const approveTransaction = useCallback(async (transactionId: string, userId?: string): Promise<Transaction> => {
    try {
      const updatedTransaction = await transactionService.approveTransaction(transactionId, userId);
      
      // Update main transaction list
      updateItem(updatedTransaction, (transaction) => transaction.id);
      
      // Update status-based lists
      setReviewTransactionsList(prev => prev.filter(t => t.id !== transactionId));
      setApprovedTransactionsList(prev => [...prev, updatedTransaction]);
      
      return updatedTransaction;
    } catch (error) {
      console.error('Failed to approve transaction:', error);
      throw error;
    }
  }, [updateItem]);

  /**
   * Decline a transaction
   */
  const declineTransaction = useCallback(async (transactionId: string, userId?: string, reason?: string): Promise<Transaction> => {
    try {
      const updatedTransaction = await transactionService.declineTransaction(transactionId, userId, reason);
      
      // Update main transaction list
      updateItem(updatedTransaction, (transaction) => transaction.id);
      
      // Update status-based lists
      setReviewTransactionsList(prev => prev.filter(t => t.id !== transactionId));
      setDeclinedTransactionsList(prev => [...prev, updatedTransaction]);
      
      return updatedTransaction;
    } catch (error) {
      console.error('Failed to decline transaction:', error);
      throw error;
    }
  }, [updateItem]);

  /**
   * Load transactions with UNDER_REVIEW status
   */
  const loadReviewTransactions = useCallback(async (pagination?: { limit?: number; offset?: number }): Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }> => {
    try {
      const result = await transactionService.getReviewTransactions(pagination);
      // Only update the state if this is the first page (offset 0 or undefined)
      if (!pagination?.offset || pagination.offset === 0) {
        setReviewTransactionsList(result.transactions);
      }
      return result;
    } catch (error) {
      console.error('Failed to load review transactions:', error);
      throw error;
    }
  }, []);

  /**
   * Load transactions with APPROVED status
   */
  const loadApprovedTransactions = useCallback(async (pagination?: { limit?: number; offset?: number }): Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }> => {
    try {
      const result = await transactionService.getApprovedTransactions(pagination);
      // Only update the state if this is the first page (offset 0 or undefined)
      if (!pagination?.offset || pagination.offset === 0) {
        setApprovedTransactionsList(result.transactions);
      }
      return result;
    } catch (error) {
      console.error('Failed to load approved transactions:', error);
      throw error;
    }
  }, []);

  /**
   * Load transactions with DECLINED status
   */
  const loadDeclinedTransactions = useCallback(async (pagination?: { limit?: number; offset?: number }): Promise<{ transactions: Transaction[]; totalCount: number; hasMore: boolean }> => {
    try {
      const result = await transactionService.getDeclinedTransactions(pagination);
      // Only update the state if this is the first page (offset 0 or undefined)
      if (!pagination?.offset || pagination.offset === 0) {
        setDeclinedTransactionsList(result.transactions);
      }
      return result;
    } catch (error) {
      console.error('Failed to load declined transactions:', error);
      throw error;
    }
  }, []);

  /**
   * Get transaction status counts
   */
  const getStatusCounts = useCallback(async (): Promise<any> => {
    try {
      return await transactionService.getStatusCounts();
    } catch (error) {
      console.error('Failed to get status counts:', error);
      throw error;
    }
  }, []);

  /**
   * Split a transaction into multiple child transactions
   */
  const splitTransaction = useCallback(async (request: { transactionId: string; splits: any[] }): Promise<any> => {
    try {
      const result = await transactionService.splitTransaction(request);
      
      // Refresh transaction list to show the updated data
      await fetchTransactionsInternal();
      setLastSyncTimestamp(transactionService.getLastSyncTimestamp());
      
      return result;
    } catch (error) {
      console.error('Failed to split transaction:', error);
      throw error;
    }
  }, [fetchTransactionsInternal]);

  /**
   * Get child transactions for a parent transaction
   */
  const getTransactionSplits = useCallback(async (parentId: string): Promise<Transaction[]> => {
    try {
      return await transactionService.getChildTransactions(parentId);
    } catch (error) {
      console.error('Failed to get transaction splits:', error);
      throw error;
    }
  }, []);

  // Category management is now handled by GL accounts system

  // Change the selected currency
  const changeCurrency = useCallback(async (currencyCode: string): Promise<void> => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currency);
    }
  }, [currencies]);

  // Format an amount based on the selected currency
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency ? selectedCurrency.symbol : '',
      currencyDisplay: 'symbol'
    }).format(amount);
  };


  /**
   * Load all accounts from backend
   */
  const loadAccounts = useCallback(async (): Promise<Account[]> => {
    try {
      setAccountsLoading(true);
      setAccountsError(null);
      const backendAccounts = await accountService.getAll();
      setAccounts(backendAccounts);
      return backendAccounts;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load accounts';
      setAccountsError(errorMessage);
      console.error('Failed to load accounts:', error);
      throw error;
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  /**
   * Load currency-aware accounts from backend with conversion and aggregated totals
   */
  const loadCurrencyAwareAccounts = useCallback(async (targetCurrency?: string): Promise<AccountBalancesAggregatedResponse> => {
    try {
      setCurrencyAwareAccountsLoading(true);
      setCurrencyAwareAccountsError(null);
      const currencyCode = targetCurrency || selectedCurrency?.code || 'USD';
      const aggregatedData = await transactionApi.getAccountBalances(currencyCode);
      setCurrencyAwareAccountData(aggregatedData);
      return aggregatedData;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load currency-aware accounts';
      setCurrencyAwareAccountsError(errorMessage);
      console.error('Failed to load currency-aware accounts:', error);
      throw error;
    } finally {
      setCurrencyAwareAccountsLoading(false);
    }
  }, [selectedCurrency?.code]);

  // Load currency-aware accounts on mount (similar to transactions)
  useEffect(() => {
    let mounted = true;

    const loadInitialAccounts = async () => {
      try {
        await loadCurrencyAwareAccounts();
      } catch (error) {
        if (mounted) {
          console.error('Failed to load currency-aware accounts on mount:', error);
        }
      }
    };

    loadInitialAccounts();

    return () => {
      mounted = false;
    };
  }, [loadCurrencyAwareAccounts]);

  /**
   * Add a new account using backend service (backward compatible)
   */
  const addAccount = useCallback(async (account: Omit<Account, 'id' | 'lastUpdated'>): Promise<void> => {
    try {
      setAccountsError(null);
      const createData: CreateAccountInput = {
        name: account.name,
        type: account.type,
        institution: account.institution,
        balance: account.balance,
        currency: account.currency
      };
      
      const newAccount = await accountService.create(createData);
      setAccounts(prevAccounts => [...prevAccounts, newAccount]);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to create account';
      setAccountsError(errorMessage);
      console.error('Failed to create account:', error);
      throw error;
    }
  }, []);
  
  /**
   * Update an existing account using backend service (backward compatible)
   */
  const updateAccount = useCallback(async (id: string, updatedFields: Partial<Account>): Promise<void> => {
    try {
      setAccountsError(null);
      const updateData: UpdateAccountInput = {};
      
      // Only include fields that are defined in updatedFields
      if (updatedFields.name !== undefined) updateData.name = updatedFields.name;
      if (updatedFields.type !== undefined) updateData.type = updatedFields.type;
      if (updatedFields.institution !== undefined) updateData.institution = updatedFields.institution;
      if (updatedFields.balance !== undefined) updateData.balance = updatedFields.balance;
      if (updatedFields.currency !== undefined) updateData.currency = updatedFields.currency;
      if (updatedFields.isActive !== undefined) updateData.isActive = updatedFields.isActive;
      
      const updatedAccount = await accountService.update(id, updateData);
      setAccounts(prevAccounts => 
        prevAccounts.map(account => 
          account.id === id ? updatedAccount : account
        )
      );
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update account';
      setAccountsError(errorMessage);
      console.error('Failed to update account:', error);
      throw error;
    }
  }, []);
  
  /**
   * Delete an account using backend service (backward compatible)
   */
  const deleteAccount = useCallback(async (id: string): Promise<void> => {
    try {
      setAccountsError(null);
      
      // Check if account has associated transactions
      const hasTransactions = (transactions || []).some(t => t.account && typeof t.account === 'object' && 'id' in t.account && t.account.id === id);
      
      if (hasTransactions) {
        throw new Error("Cannot delete account with associated transactions");
      }
      
      await accountService.delete(id);
      setAccounts(prevAccounts => prevAccounts.filter(account => account.id !== id));
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to delete account';
      setAccountsError(errorMessage);
      console.error('Failed to delete account:', error);
      throw error;
    }
  }, [transactions]);

  /**
   * Get account by ID using backend service
   */
  const getAccountById = useCallback(async (id: string): Promise<Account | null> => {
    try {
      setAccountsError(null);
      return await accountService.getById(id);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to get account';
      setAccountsError(errorMessage);
      console.error('Failed to get account:', error);
      throw error;
    }
  }, []);

  /**
   * Get account count using backend service
   */
  const getAccountCount = useCallback(async (filters?: AccountFilters): Promise<number> => {
    try {
      setAccountsError(null);
      return await accountService.getCount(filters);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to get account count';
      setAccountsError(errorMessage);
      console.error('Failed to get account count:', error);
      throw error;
    }
  }, []);

  /**
   * Check if account exists using backend service
   */
  const checkAccountExists = useCallback(async (name: string, institution: string): Promise<boolean> => {
    try {
      setAccountsError(null);
      return await accountService.checkExists(name, institution);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to check account existence';
      setAccountsError(errorMessage);
      console.error('Failed to check account existence:', error);
      throw error;
    }
  }, []);

  /**
   * Refresh accounts from backend
   */
  const refreshAccounts = useCallback(async (): Promise<void> => {
    try {
      const refreshedAccounts = await accountService.refresh();
      setAccounts(refreshedAccounts);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to refresh accounts';
      setAccountsError(errorMessage);
      console.error('Failed to refresh accounts:', error);
      throw error;
    }
  }, []);


  // ==========================================
  // PLAID FUNCTIONS
  // ==========================================

  /**
   * Load all Plaid connections for the organization
   */
  const loadPlaidConnections = useCallback(async (): Promise<PlaidConnection[]> => {
    try {
      setPlaidConnectionsLoading(true);
      setPlaidConnectionsError(null);
      
      const response = await plaidApi.getPlaidConnections({ includeInactive: false });
      const connections = Array.isArray(response) ? response : (response.connections || []);
      
      setPlaidConnections(connections);
      return connections;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load Plaid connections';
      setPlaidConnectionsError(errorMessage);
      console.error('Failed to load Plaid connections:', error);
      throw error;
    } finally {
      setPlaidConnectionsLoading(false);
    }
  }, []);

  /**
   * Sync transactions from Plaid for a specific connection or all connections
   */
  const syncPlaidTransactions = useCallback(async (connectionId?: string): Promise<SyncTransactionsResponse> => {
    try {
      // Ensure connectionId is provided
      if (!connectionId) {
        throw new Error('Connection ID is required for syncing transactions');
      }

      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: true,
        error: undefined,
        progress: {
          current: 0,
          total: 1,
          message: 'Syncing connection...'
        }
      }));
      
      const response = await plaidApi.syncPlaidTransactions({ connectionId });
      
      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date(),
        result: response,
        progress: undefined
      }));
      
      // Update accounts state with fresh data from sync response
      if (response.finStackAccounts && response.finStackAccounts.length > 0) {
        setAccounts(prevAccounts => {
          const updatedAccountsMap = new Map(
            response.finStackAccounts!.map(acc => [acc.id, acc])
          );
          
          // Merge updated accounts with existing ones
          return prevAccounts.map(acc => 
            updatedAccountsMap.has(acc.id) 
              ? updatedAccountsMap.get(acc.id)! 
              : acc
          );
        });
        
        console.log(`Updated ${response.finStackAccounts.length} account balances from Plaid sync`);
      }
      
      // Refresh main transactions list if sync was successful
      if (response.autoApproved > 0 || response.needsReview > 0) {
        await refreshTransactions();
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to sync Plaid transactions';
      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: false,
        error: errorMessage,
        progress: undefined
      }));
      console.error('Failed to sync Plaid transactions:', error);
      throw error;
    }
  }, [refreshTransactions]);

  /**
   * Connect a new Plaid account using the public token
   */
  const connectPlaidAccount = useCallback(async (publicToken: string, metadata?: PlaidLinkMetadata): Promise<ExchangePublicTokenResponse> => {
    try {
      const response = await plaidApi.exchangePlaidPublicToken({
        publicToken,
        metadata
      });
      
      // Response contains connection, accounts, and institution objects
      if (response.connection) {
        // Refresh connections list to include the new connection
        await loadPlaidConnections();
        
        // Return the expected format for the frontend
        return {
          success: true,
          itemId: response.connection.id,
          message: `Successfully connected ${response.institution?.name || 'bank account'}`
        };
      } else {
        throw new Error('Invalid response from Plaid service');
      }
    } catch (error: any) {
      console.error('Failed to connect Plaid account:', error);
      throw error;
    }
  }, [loadPlaidConnections]);

  /**
   * Disconnect a Plaid account connection
   */
  const disconnectPlaidAccount = useCallback(async (itemId: string, reason?: string): Promise<DisconnectAccountResponse> => {
    try {
      const response = await plaidApi.disconnectPlaidAccount({ itemId, reason });
      
      if (response.success) {
        // Remove the connection from local state
        setPlaidConnections(prev => prev.filter(conn => conn.itemId !== itemId));
      }
      
      return response;
    } catch (error: any) {
      console.error('Failed to disconnect Plaid account:', error);
      throw error;
    }
  }, []);

  // Debounce map to prevent multiple simultaneous status requests
  const statusRequestsRef = useRef<Map<string, Promise<GetConnectionStatusResponse>>>(new Map());

  /**
   * Get the status of a specific Plaid connection with debouncing
   */
  const getPlaidStatus = useCallback(async (connectionId: string): Promise<GetConnectionStatusResponse> => {
    try {
      // Check if there's already a pending request for this connection
      const existingRequest = statusRequestsRef.current.get(connectionId);
      if (existingRequest) {
        console.debug(`Reusing existing status request for connection: ${connectionId}`);
        return existingRequest;
      }

      // Create new request
      const request = plaidApi.getPlaidConnectionStatus({ connectionId });
      statusRequestsRef.current.set(connectionId, request);
      
      const response = await request;
      
      // Update the connection status in local state
      setPlaidConnections(prev => 
        prev.map(conn => 
          conn.id === connectionId 
            ? {
                ...conn,
                status: response.status.status,
                needsReauth: response.status.needsReauth,
                lastSyncTime: response.status.lastSync,
                error: response.status.error?.error_message
              }
            : conn
        )
      );
      
      // Clean up the request from the map
      statusRequestsRef.current.delete(connectionId);
      
      return response;
    } catch (error: any) {
      // Clean up the request from the map on error
      statusRequestsRef.current.delete(connectionId);
      console.error('Failed to get Plaid connection status:', error);
      throw error;
    }
  }, []);

  /**
   * Load Plaid account balances
   */
  const loadPlaidAccountBalances = useCallback(async (connectionId?: string): Promise<PlaidAccountBalance[]> => {
    try {
      setPlaidAccountBalancesLoading(true);
      setPlaidAccountBalancesError(null);
      
      const response = await plaidApi.getPlaidAccountBalances({ connectionId });
      const balances = response.balances || [];
      
      setPlaidAccountBalances(balances);
      return balances;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load Plaid account balances';
      setPlaidAccountBalancesError(errorMessage);
      console.error('Failed to load Plaid account balances:', error);
      throw error;
    } finally {
      setPlaidAccountBalancesLoading(false);
    }
  }, []);

  /**
   * Bulk sync transactions from multiple Plaid connections
   */
  const bulkSyncPlaidTransactions = useCallback(async (connectionIds?: string[], forceFullSync?: boolean): Promise<BulkSyncTransactionsResponse> => {
    try {
      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: true,
        error: undefined,
        progress: {
          current: 0,
          total: connectionIds?.length || plaidConnections.length,
          message: 'Starting bulk sync...'
        }
      }));
      
      const response = await plaidApi.bulkSyncPlaidTransactions({ 
        connectionIds, 
        forceFullSync 
      });
      
      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date(),
        result: {
          autoApproved: response.totalAdded,
          needsReview: response.totalModified,
          failed: (response.errors || []).length,
          errors: response.errors || [],
          modified: response.totalModified,
          removed: response.totalRemoved
        },
        progress: undefined
      }));
      
      // Refresh main transactions list if sync added transactions
      if (response.totalAdded > 0 || response.totalModified > 0) {
        await refreshTransactions();
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to bulk sync Plaid transactions';
      setPlaidSyncState(prev => ({
        ...prev,
        isSyncing: false,
        error: errorMessage,
        progress: undefined
      }));
      console.error('Failed to bulk sync Plaid transactions:', error);
      throw error;
    }
  }, [plaidConnections.length, refreshTransactions]);

  /**
   * Create a Plaid Link token for connecting new accounts
   */
  const createPlaidLinkToken = useCallback(async (): Promise<CreateLinkTokenResponse> => {
    try {
      // Provide default request data for Plaid Link token creation
      const request = {
        clientName: 'FinStack',
        products: ['transactions'],
        countryCodes: ['US'],
        language: 'en',
      };
      const response = await plaidApi.createPlaidLinkToken(request);
      return response;
    } catch (error: any) {
      console.error('Failed to create Plaid Link token:', error);
      throw error;
    }
  }, []);

  /**
   * Test Plaid connection and service availability
   */
  const testPlaidConnection = useCallback(async (): Promise<boolean> => {
    try {
      const response = await plaidApi.testPlaidConnection();
      return response.serviceAvailable;
    } catch (error: any) {
      console.error('Failed to test Plaid connection:', error);
      return false;
    }
  }, []);

  // Load Plaid connections on mount
  useEffect(() => {
    let mounted = true;
    
    const loadInitialPlaidData = async () => {
      try {
        await loadPlaidConnections();
      } catch (error) {
        if (mounted) {
          console.error('Failed to load initial Plaid connections:', error);
        }
      }
    };

    loadInitialPlaidData();

    return () => {
      mounted = false;
    };
  }, [loadPlaidConnections]);

  // Monitor Plaid connection health - DISABLED to reduce log pollution
  // The PlaidHealthMonitor component now handles all health monitoring
  useEffect(() => {
    // Health check disabled - use PlaidHealthMonitor component instead
    // This was causing getPlaidConnectionStatus to run every minute and pollute logs
    return;
  }, [plaidConnections, getPlaidStatus]);

  // ==========================================
  // MONO INTEGRATION FUNCTIONS
  // ==========================================

  /**
   * Load all Mono connections for the organization
   */
  const loadMonoConnections = useCallback(async (): Promise<MonoConnection[]> => {
    try {
      setMonoConnectionsLoading(true);
      setMonoConnectionsError(null);
      
      const connections = await monoApi.getConnections();
      setMonoConnections(connections);
      return connections;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load Mono connections';
      setMonoConnectionsError(errorMessage);
      console.error('Failed to load Mono connections:', error);
      throw error;
    } finally {
      setMonoConnectionsLoading(false);
    }
  }, []);

  /**
   * Sync transactions from Mono for specific connections
   */
  const syncMonoTransactions = useCallback(async (connectionIds?: string[]): Promise<BulkSyncMonoTransactionsResponse> => {
    try {
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: true,
        error: undefined,
        progress: {
          current: 0,
          total: connectionIds?.length || monoConnections.length,
          message: 'Syncing Mono transactions...'
        }
      }));
      
      const response = await monoApi.syncTransactions({ 
        connectionIds,
        includeInactive: false
      });
      
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: false,
        result: response,
        progress: undefined
      }));
      
      // Update accounts state with fresh data from sync response
      if (response.results && response.results.length > 0) {
        const allUpdatedAccounts: Account[] = [];
        response.results.forEach(result => {
          if (result.updatedAccounts && result.updatedAccounts.length > 0) {
            allUpdatedAccounts.push(...result.updatedAccounts);
          }
        });
        
        if (allUpdatedAccounts.length > 0) {
          setAccounts(prevAccounts => {
            const updatedAccountsMap = new Map(
              allUpdatedAccounts.map(acc => [acc.id, acc])
            );
            
            // Merge updated accounts with existing ones
            return prevAccounts.map(acc => 
              updatedAccountsMap.has(acc.id) 
                ? updatedAccountsMap.get(acc.id)! 
                : acc
            );
          });
          
          console.log(`Updated ${allUpdatedAccounts.length} account balances from Mono sync`);
        }
      }
      
      // Refresh main transactions list if sync was successful
      if (response.successCount > 0) {
        await refreshTransactions();
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to sync Mono transactions';
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: false,
        error: errorMessage,
        progress: undefined
      }));
      console.error('Failed to sync Mono transactions:', error);
      throw error;
    }
  }, [monoConnections.length, refreshTransactions]);

  /**
   * Connect a new Mono account using the authorization code
   */
  const connectMonoAccount = useCallback(async (authCode: string): Promise<ExchangeMonoTokenResponse> => {
    try {
      const response = await monoApi.exchangeAuthToken({ code: authCode });
      
      // Refresh connections list to include the new connection
      await loadMonoConnections();
      
      return response;
    } catch (error: any) {
      console.error('Failed to connect Mono account:', error);
      throw error;
    }
  }, [loadMonoConnections]);

  /**
   * Disconnect a Mono account connection
   */
  const disconnectMonoAccount = useCallback(async (connectionId: string): Promise<DisconnectMonoResponse> => {
    try {
      const response = await monoApi.deleteConnection({ 
        connectionId,
        removeData: false 
      });
      
      if (response.status === 'disconnected') {
        // Remove the connection from local state
        setMonoConnections(prev => prev.filter(conn => conn.id !== connectionId));
      }
      
      return response;
    } catch (error: any) {
      console.error('Failed to disconnect Mono account:', error);
      throw error;
    }
  }, []);

  /**
   * Bulk sync transactions from multiple Mono connections
   */
  const bulkSyncMonoTransactions = useCallback(async (connectionIds?: string[], forceFullSync?: boolean): Promise<BulkSyncMonoTransactionsResponse> => {
    try {
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: true,
        error: undefined,
        progress: {
          current: 0,
          total: connectionIds?.length || monoConnections.length,
          message: 'Starting bulk Mono sync...'
        }
      }));
      
      const requestData: any = { 
        connectionIds,
        includeInactive: false
      };
      
      // Only include maxAge if forceFullSync is true
      if (forceFullSync) {
        requestData.maxAge = 0;
      }
      
      const response = await monoApi.syncTransactions(requestData);
      
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: false,
        result: response,
        progress: undefined
      }));
      
      // Update accounts state with fresh data from sync response
      if (response.results && response.results.length > 0) {
        const allUpdatedAccounts: Account[] = [];
        response.results.forEach(result => {
          if (result.updatedAccounts && result.updatedAccounts.length > 0) {
            allUpdatedAccounts.push(...result.updatedAccounts);
          }
        });
        
        if (allUpdatedAccounts.length > 0) {
          setAccounts(prevAccounts => {
            const updatedAccountsMap = new Map(
              allUpdatedAccounts.map(acc => [acc.id, acc])
            );
            
            // Merge updated accounts with existing ones
            return prevAccounts.map(acc => 
              updatedAccountsMap.has(acc.id) 
                ? updatedAccountsMap.get(acc.id)! 
                : acc
            );
          });
          
          console.log(`Updated ${allUpdatedAccounts.length} account balances from bulk Mono sync`);
        }
      }
      
      // Refresh main transactions list if sync added transactions
      if (response.successCount > 0) {
        await refreshTransactions();
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to bulk sync Mono transactions';
      setMonoSyncState(prev => ({
        ...prev,
        isSyncing: false,
        error: errorMessage,
        progress: undefined
      }));
      console.error('Failed to bulk sync Mono transactions:', error);
      throw error;
    }
  }, [monoConnections.length, refreshTransactions]);

  // Load Mono connections on mount
  useEffect(() => {
    let mounted = true;
    
    const loadInitialMonoData = async () => {
      try {
        await loadMonoConnections();
      } catch (error) {
        if (mounted) {
          console.error('Failed to load initial Mono connections:', error);
        }
      }
    };

    loadInitialMonoData();

    return () => {
      mounted = false;
    };
  }, [loadMonoConnections]);

  const contextValue: FinanceContextType = {
    // State
    transactions: transactions || [],
    loading,
    error,
    lastSyncTimestamp,
    
    // Alias properties for backward compatibility
    transactionsLoading: loading,
    transactionsError: error,
    
    // Status-based transaction arrays
    statusReviewTransactions: reviewTransactionsList,
    statusApprovedTransactions: approvedTransactionsList,
    statusDeclinedTransactions: declinedTransactionsList,
    
    // Actions
    loadTransactions,
    createTransaction,
    updateTransaction, // Use the direct updateTransaction function that doesn't require finding the transaction first
    deleteTransaction,
    deleteTransactions,
    deleteMultipleTransactions: deleteTransactions, // Alias for backward compatibility
    searchTransactions,
    refreshTransactions,
    bulkImportTransactions,
    
    // Status-based transaction operations
    approveTransaction,
    declineTransaction,
    loadReviewTransactions,
    loadApprovedTransactions,
    loadDeclinedTransactions,
    getStatusCounts,
    
    // Legacy method for backward compatibility
    addTransaction,
    
    // Split transaction operations
    splitTransaction,
    getTransactionSplits,
    
    // Cache operations
    getCacheMetrics,
    getCacheHealth,
    warmCache,
    resetCacheMetrics,
    
    // Currency operations
    currencies,
    currenciesLoading,
    currenciesError,
    enabledCurrencies,
    selectedCurrency: selectedCurrency || 
      enabledCurrencies.find(c => c.code === 'USD') || 
      (enabledCurrencies.length > 0 ? enabledCurrencies[0] : { code: '', symbol: '', name: '', exchangeRate: 1 }),
    changeCurrency,
    formatAmount,
    
    // Import state management
    reviewTransactions,
    setReviewTransactions,
    showImportReview,
    setShowImportReview,
    
    // Account state
    accounts,
    accountsLoading,
    accountsError,
    
    // Currency-aware account state
    currencyAwareAccountData,
    currencyAwareAccountsLoading,
    currencyAwareAccountsError,
    
    // Legacy state (kept for backward compatibility)
    filters,
    setFilters,
    financialRunway,
    reconciliationStatus,
    accountReconciliationData,
    dailyFinancialData: dashboardData.dailyData,
    monthlyFinancialData: dashboardData.monthlyData,
    dashboardLoading,
    dashboardError,
    
    // Dashboard data operations
    loadDashboardData,
    
    // Financial runway operations
    loadFinancialRunway,
    
    // Account operations
    loadAccounts,
    loadCurrencyAwareAccounts,
    addAccount,
    updateAccount,
    deleteAccount,
    getAccountById,
    getAccountCount,
    checkAccountExists,
    refreshAccounts,
    
    // Plaid functionality
    plaidConnections,
    plaidConnectionsLoading,
    plaidConnectionsError,
    plaidSyncState,
    plaidAccountBalances,
    plaidAccountBalancesLoading,
    plaidAccountBalancesError,
    
    // Plaid operations
    loadPlaidConnections,
    syncPlaidTransactions,
    connectPlaidAccount,
    disconnectPlaidAccount,
    getPlaidStatus,
    loadPlaidAccountBalances,
    bulkSyncPlaidTransactions,
    createPlaidLinkToken,
    testPlaidConnection,
    
    // Mono functionality
    monoConnections,
    monoConnectionsLoading,
    monoConnectionsError,
    monoSyncState,
    
    // Mono operations
    loadMonoConnections,
    syncMonoTransactions,
    connectMonoAccount,
    disconnectMonoAccount,
    bulkSyncMonoTransactions,
    
    // Utility
    reset
  };

  return (
    <FinanceContext.Provider value={contextValue}>
      {children}
    </FinanceContext.Provider>
  );
};

/**
 * Hook to use Finance context
 * Throws error if used outside of FinanceProvider
 */
export const useFinance = (): FinanceContextType => {
  const context = useContext(FinanceContext);
  if (context === undefined) {
    throw new Error('useFinance must be used within a FinanceProvider');
  }
  return context;
};

/**
 * Hook for transaction operations with optimistic updates and error handling
 */
export const useTransactionOperations = () => {
  const context = useFinance();
  
  const addTransactionWithValidation = useCallback(async (transaction: any) => {
    // Basic validation
    if (!transaction.description?.trim()) {
      throw new Error('Transaction description is required');
    }
    if (transaction.amount === undefined || transaction.amount === null) {
      throw new Error('Transaction amount is required');
    }
    if (!transaction.type) {
      throw new Error('Transaction type is required');
    }
    
    return context.addTransaction(transaction);
  }, [context]);
  
  const updateTransactionWithValidation = useCallback(async (id: string, data: Omit<TransactionUpdateData, 'id'>) => {
    if (!id) {
      throw new Error('Transaction ID is required for update');
    }
    
    return context.updateTransaction(id, data);
  }, [context]);
  
  return {
    ...context,
    addTransaction: addTransactionWithValidation,
    updateTransaction: updateTransactionWithValidation
  };
};

export default FinanceContext;
